import type { ReactNode } from 'react'
import { StatusPill } from '@/components/ui'
import { palletStatusLabel } from '@/lib/palletStatus'
import { Link } from 'react-router-dom'
import clsx from 'clsx'
import { List, Table } from 'lucide-react'
import { toCSV } from '@/lib/csv'
import { cellOrOverflow } from '@/lib/coldroomPlacement'

export type PalletView = 'cards' | 'table'

// Segmented Cards / Table control — shared by every surface that offers the
// pallet table view so the toggle looks and behaves identically.
export function PalletViewToggle({ value, onChange }: { value: PalletView; onChange: (v: PalletView) => void }) {
  const btn = (v: PalletView, icon: ReactNode, label: string) => (
    <button
      type="button"
      onClick={() => onChange(v)}
      aria-pressed={value === v}
      className={clsx(
        'tap-target-y px-2.5 py-1.5 flex items-center gap-1 text-xs font-semibold transition-colors',
        value === v ? 'surface-raised text-fg' : 'surface-sunk text-fg-muted hover:text-fg',
      )}
      title={`${label} view`}>
      {icon} {label}
    </button>
  )
  return (
    <div className="flex items-center rounded-lg border hairline-strong overflow-hidden shrink-0">
      {btn('cards', <List size={14} />, 'Cards')}
      {btn('table', <Table size={14} />, 'Table')}
    </div>
  )
}

// Excel-style column view for a list of pallets. Each pallet field gets its
// own column + header so an operator can scan a run (or all pallets) like a
// spreadsheet, as an alternative to the stacked cards.
//
// Used by both the Runs detail "Pallets" tab and the All-pallets view, so it
// accepts a loose row shape: every field is optional except the id/status the
// table always shows. Columns whose value is empty for *every* row in the set
// are dropped automatically, so each surface only renders the columns its
// query actually populates — no sea of em-dashes.

export interface PalletTableRow {
  pallet_id: string
  status: string
  packed_at?: string | null
  born_date?: string | null
  pallet_type?: string | null
  spec?: string | null
  grade?: string | null
  size_code?: string | null
  mark?: string | null
  regime_code?: string | null
  target_market?: string | null
  destination?: string | null
  pick_no?: string | null
  pack_type?: string | null
  carton_count?: number | null
  net_weight_kg?: number | null
  gross_weight_kg?: number | null
  coldroom_grid?: string | null
  coldroom_overflow?: boolean | null
  loading_bay_lane?: string | null
  ssc_id?: string | null
  intake_no?: string | null
  consignment_no?: string | null
  truck_consignment_number?: string | null
  ppecb_consignment_number?: string | null
  mission_number?: string | null
  line_count?: number | null
  // Cross-run context — only the All-pallets view passes these.
  run_number?: string | null
  grower?: string | null
  variety?: string | null
}

interface ColumnDef {
  key:        string
  header:     string
  // Cell text, or null when this pallet has no value (used for auto-hide).
  get:        (r: PalletTableRow) => string | null
  align?:     'right'
  mono?:      boolean
  className?: string
}

const fmtKg  = (v: number | null | undefined) => (v == null ? null : `${v.toFixed(1)}`)
const fmtInt = (v: number | null | undefined) => (v == null ? null : `${v}`)

const COLUMNS: ColumnDef[] = [
  { key: 'run',       header: 'Run',         get: r => r.run_number ?? null,  mono: true },
  { key: 'grower',    header: 'Grower',      get: r => r.grower ?? null },
  { key: 'variety',   header: 'Variety',     get: r => r.variety ?? null },
  { key: 'type',      header: 'Type',        get: r => r.pallet_type ?? r.spec ?? null },
  { key: 'grade',     header: 'Grade',       get: r => r.grade ?? null },
  { key: 'size',      header: 'Size',        get: r => r.size_code ?? null },
  { key: 'mark',      header: 'Mark',        get: r => r.mark ?? null },
  { key: 'regime',    header: 'Regime',      get: r => r.regime_code ?? null },
  { key: 'market',    header: 'Market',      get: r => r.target_market ?? null },
  { key: 'dest',      header: 'Destination', get: r => r.destination ?? null },
  { key: 'pick',      header: 'Pick #',      get: r => r.pick_no ?? null,     mono: true },
  { key: 'pack',      header: 'Pack type',   get: r => r.pack_type ?? null },
  { key: 'cartons',   header: 'Cartons',     get: r => fmtInt(r.carton_count),    align: 'right' },
  { key: 'net',       header: 'Net kg',      get: r => fmtKg(r.net_weight_kg),    align: 'right' },
  { key: 'gross',     header: 'Gross kg',    get: r => fmtKg(r.gross_weight_kg),  align: 'right' },
  // 'Overflow' when the pallet is in a room but deliberately off its grid
  // (mig 20261021100001); blank, as before, for a pallet with no location.
  { key: 'grid',      header: 'Grid',        get: r => cellOrOverflow(r),         mono: true },
  { key: 'lane',      header: 'Lane',        get: r => r.loading_bay_lane ?? null },
  // Build-up: only meaningful when split across >1 line.
  { key: 'lines',     header: 'Lines',       get: r => ((r.line_count ?? 1) > 1 ? `${r.line_count}` : null), align: 'right' },
  { key: 'ssc',       header: 'SSC barcode', get: r => r.ssc_id ?? null,          mono: true },
  { key: 'intake',    header: 'Intake',      get: r => r.intake_no ?? null,       mono: true },
  { key: 'cons',      header: 'Consignment', get: r => r.truck_consignment_number ?? r.consignment_no ?? null, mono: true },
  { key: 'ppecb',     header: 'PPECB',       get: r => r.ppecb_consignment_number ?? null, mono: true },
  { key: 'mission',   header: 'Mission',     get: r => r.mission_number ?? null,  mono: true },
  { key: 'packed',    header: 'Packed',      get: r => (r.packed_at ? new Date(r.packed_at).toLocaleString('en-ZA', { dateStyle: 'short', timeStyle: 'short' }) : null) },
  // The fruit's true age anchor (berry cool-start / inherited bin born_date,
  // FC-19). Empty-column auto-drop hides it on surfaces that don't select it.
  { key: 'born',      header: 'Fruit born',  get: r => (r.born_date ? new Date(r.born_date).toLocaleString('en-ZA', { dateStyle: 'short', timeStyle: 'short' }) : null) },
]

// CSV body for a pallet list, mirroring the *visible* table columns (empty
// columns dropped, same rule as the table) so "what you see is what you
// export". Reuses COLUMNS so the export and the grid never drift apart.
// Values are formula-injection-safe via csvCell inside toCSV.
export function palletsToCsvBody(pallets: PalletTableRow[]): string {
  const cols = COLUMNS.filter(c => pallets.some(p => c.get(p) != null))
  const headers = ['Pallet', 'Status', ...cols.map(c => c.header)]
  return toCSV(headers, pallets, p => [
    p.pallet_id,
    palletStatusLabel(p.status),
    ...cols.map(c => c.get(p) ?? ''),
  ])
}

export default function PalletTable<T extends PalletTableRow>({
  pallets,
  selectable = false,
  isRowSelectable,
  selectedIds,
  onToggleSelect,
}: {
  pallets: T[]
  // Optional row selection — used by the Runs tab's "move pallets" flow.
  // Generic over the row type so the caller can pass its own richer pallet
  // shape (e.g. Runs' Pallet) to the eligibility predicate without a cast.
  selectable?: boolean
  isRowSelectable?: (p: T) => boolean
  selectedIds?: Set<string>
  onToggleSelect?: (id: string) => void
}) {
  // Drop columns that are empty for every row so each surface shows only the
  // fields its query populated.
  const cols = COLUMNS.filter(c => pallets.some(p => c.get(p) != null))

  // z-10/z-20 ties the sticky PageHeader and comes later in the DOM, so the scroll
  // wrapper below is `relative isolate`: without it this column paints over the
  // header once scrolled and a tap on Back opens a row (packhouse audit PW-03).
  const stickyCell = 'sticky left-0 z-10 surface-sunk group-hover:bg-surface/60'

  return (
    <div className="relative isolate overflow-x-auto -mx-4 px-4 pb-2">
      <table className="min-w-full text-xs border-collapse">
        <thead>
          <tr className="text-left text-[10px] uppercase tracking-wider text-fg-muted">
            {selectable && <th className="px-2 py-2 w-8" />}
            <th className={clsx('px-2 py-2 font-semibold', stickyCell, 'z-20')}>Pallet</th>
            <th className="px-2 py-2 font-semibold">Status</th>
            {cols.map(c => (
              <th key={c.key} className={clsx('px-2 py-2 font-semibold whitespace-nowrap', c.align === 'right' && 'text-right')}>
                {c.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {pallets.map(p => {
            const selectRow = selectable && (isRowSelectable?.(p) ?? true)
            const checked   = selectedIds?.has(p.pallet_id) ?? false
            return (
              <tr
                key={p.pallet_id}
                className={clsx('group border-t hairline', checked && 'bg-blue-900/20')}>
                {selectable && (
                  <td className="px-2 py-1.5 align-middle">
                    {selectRow && (
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => onToggleSelect?.(p.pallet_id)}
                        aria-label={`Select pallet ${p.pallet_id}`}
                        title="Select to move to another run"
                        className="h-4 w-4 accent-blue-500 cursor-pointer" />
                    )}
                  </td>
                )}
                <td className={clsx('px-2 py-1.5 whitespace-nowrap', stickyCell, checked && '!bg-blue-900/20')}>
                  <Link
                    to={`/pallets/${p.pallet_id}`}
                    className="font-mono font-bold text-fg hover:text-blue-300 hover:underline">
                    {p.pallet_id}
                  </Link>
                </td>
                <td className="px-2 py-1.5 whitespace-nowrap">
                  <StatusPill object="pallet" status={p.status} />
                </td>
                {cols.map(c => {
                  const v = c.get(p)
                  return (
                    <td
                      key={c.key}
                      className={clsx(
                        'px-2 py-1.5 whitespace-nowrap text-fg-secondary',
                        c.align === 'right' && 'text-right tabular-nums',
                        c.mono && 'font-mono',
                      )}>
                      {v ?? <span className="text-fg-muted">—</span>}
                    </td>
                  )
                })}
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}
